import React, { useState } from 'react';
import { Sparkles, Users, ShoppingBag, Star, ChevronDown, LayoutDashboard, Shield, LogOut, User as UserIcon, BookOpen } from 'lucide-react';
import { User } from '../../../types';
import { serviceItems, knowledgeItems } from './headerData';
interface MobileMenuProps {
  isMobileMenuOpen: boolean;
  handleNavClick: (page: string) => void;
  currentPage: string;
  t: (key: string) => string;
  user: User | null;
  isLoggedIn: boolean;
  logout: () => void;
  langOptions: {
    code: string;
    label: string;
    shortLabel: string;
    flag: string;
  }[];
  language: string;
  setLanguage: (lang: string) => void;
}
export function MobileMenu({
  isMobileMenuOpen,
  handleNavClick,
  currentPage,
  t,
  user,
  isLoggedIn,
  logout,
  langOptions,
  language,
  setLanguage
}: MobileMenuProps) {
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileKnowledgeOpen, setMobileKnowledgeOpen] = useState(false);
  if (!isMobileMenuOpen) return null;
  return <div className="lg:hidden bg-white border-t border-slate-100 shadow-2xl absolute w-full max-h-[calc(100vh-64px)] overflow-y-auto" data-id="element-15992">
      <div className="p-4 space-y-2" data-id="element-15993">
        {/* Primary CTA */}
        <button onClick={() => handleNavClick('free-chart')} className="flex items-center justify-center gap-2 w-full px-4 py-3.5 text-white bg-slate-900 rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform" data-id="element-15994">
          <Sparkles className="w-4 h-4" data-id="element-15995" />
          {t('nav.freeChartFull')}
        </button>

        {/* Quick links */}
        <div className="grid grid-cols-3 gap-1.5 py-1" data-id="element-15996">
          <button onClick={() => handleNavClick('astrologers')} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-colors ${currentPage === 'astrologers' ? 'bg-indigo-50' : 'bg-slate-50 active:bg-slate-100'}`} data-id="element-15997">
            <Users className="w-5 h-5 text-slate-500" data-id="element-15998" />
            <span className="text-[11px] font-medium text-slate-600" data-id="element-15999">
              {t('nav.astrologers')}
            </span>
          </button>
          <button onClick={() => handleNavClick('marketplace')} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-colors ${currentPage === 'marketplace' ? 'bg-indigo-50' : 'bg-slate-50 active:bg-slate-100'}`} data-id="element-16000">
            <ShoppingBag className="w-5 h-5 text-slate-500" data-id="element-16001" />
            <span className="text-[11px] font-medium text-slate-600" data-id="element-16002">
              Маркетплейс
            </span>
          </button>
          <button onClick={() => handleNavClick('pricing')} className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-colors ${currentPage === 'pricing' ? 'bg-indigo-50' : 'bg-slate-50 active:bg-slate-100'}`} data-id="element-16003">
            <Star className="w-5 h-5 text-slate-500" data-id="element-16004" />
            <span className="text-[11px] font-medium text-slate-600" data-id="element-16005">
              {t('nav.pricing')}
            </span>
          </button>
        </div>

        <div className="h-px bg-slate-100" data-id="element-16006" />

        {/* Услуги — collapsible */}
        <div className="rounded-xl border border-slate-100 overflow-hidden" data-id="element-16007">
          <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="flex items-center justify-between w-full px-4 py-3 text-left" data-id="element-16008">
            <div className="flex items-center gap-3" data-id="element-16009">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-16010">
                <Star className="w-4 h-4 text-indigo-500" data-id="element-16011" />
              </div>
              <div data-id="element-16012">
                <div className="text-sm font-semibold text-slate-800" data-id="element-16013">
                  {t('mobile.services')}
                </div>
                <div className="text-[11px] text-slate-400" data-id="element-16014">
                  Отчёты, прогнозы, консультации
                </div>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-16015" />
          </button>
          {mobileServicesOpen && <div className="px-2 pb-2 space-y-0.5 border-t border-slate-50" data-id="element-16016">
              {serviceItems.map(item => <button key={item.page} onClick={() => handleNavClick(item.page)} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 active:bg-slate-50 transition-colors" data-id="element-16017">
                  {item.icon}
                  {t(item.labelKey)}
                </button>)}
            </div>}
        </div>

        {/* Знания — collapsible */}
        <div className="rounded-xl border border-slate-100 overflow-hidden" data-id="element-16018">
          <button onClick={() => setMobileKnowledgeOpen(!mobileKnowledgeOpen)} className="flex items-center justify-between w-full px-4 py-3 text-left" data-id="element-16019">
            <div className="flex items-center gap-3" data-id="element-16020">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center" data-id="element-16021">
                <BookOpen className="w-4 h-4 text-amber-500" data-id="element-16022" />
              </div>
              <div data-id="element-16023">
                <div className="text-sm font-semibold text-slate-800" data-id="element-16024">
                  Знания
                </div>
                <div className="text-[11px] text-slate-400" data-id="element-16025">
                  Календари, трактовки, блог
                </div>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform duration-200 ${mobileKnowledgeOpen ? 'rotate-180' : ''}`} data-id="element-16026" />
          </button>
          {mobileKnowledgeOpen && <div className="px-2 pb-2 space-y-0.5 border-t border-slate-50" data-id="element-16027">
              <div className="pl-4 space-y-2" data-id="element-16028">
                {knowledgeItems.map(item => <button key={item.page} onClick={() => handleNavClick(item.page)} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 active:bg-slate-50 transition-colors" data-id="element-16029">
                    {item.icon}
                    {item.labelKey.includes('.') ? t(item.labelKey) : item.labelKey}
                  </button>)}
              </div>
            </div>}
        </div>

        <div className="h-px bg-slate-100" data-id="element-16030" />

        {/* Account */}
        {isLoggedIn ? <div className="rounded-xl border border-slate-100 overflow-hidden" data-id="element-16031">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-50" data-id="element-16032">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-16033">
                <span className="text-sm font-bold text-indigo-700 uppercase" data-id="element-16034">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="min-w-0" data-id="element-16035">
                <div className="text-sm font-bold text-slate-900 truncate" data-id="element-16036">
                  {user?.name}
                </div>
                <div className="text-xs text-slate-500 truncate" data-id="element-16037">
                  {user?.email}
                </div>
              </div>
            </div>
            <div className="p-2 space-y-0.5" data-id="element-16038">
              <button onClick={() => handleNavClick(user?.role === 'astrologer' ? 'dashboard-astrologer' : 'dashboard-user')} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 active:bg-slate-50 transition-colors" data-id="element-16039">
                <LayoutDashboard className="w-4 h-4 text-slate-400" data-id="element-16040" />
                {t('profile.dashboard')}
              </button>
              {user?.role === 'admin' && <button onClick={() => handleNavClick('admin')} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 active:bg-slate-50 transition-colors" data-id="element-16041">
                  <Shield className="w-4 h-4 text-rose-500" data-id="element-16042" />
                  Админ-панель
                </button>}
              <button onClick={() => {
            logout();
            handleNavClick('home');
          }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-rose-600 active:bg-rose-50 transition-colors" data-id="element-16043">
                <LogOut className="w-4 h-4 text-rose-400" data-id="element-16044" />
                {t('profile.logout')}
              </button>
            </div>
          </div> : <div className="flex items-center gap-2" data-id="element-16045">
            <button onClick={() => handleNavClick('login')} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-white bg-indigo-600 active:bg-indigo-700 transition-colors" data-id="element-16046">
              <UserIcon className="w-4 h-4" data-id="element-16047" />
              {t('nav.login') || 'Войти'}
            </button>
            <button onClick={() => handleNavClick('for-astrologers')} className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 bg-slate-50 active:bg-slate-100 transition-colors" data-id="element-16048">
              <Star className="w-4 h-4 text-slate-400" data-id="element-16049" />
              {t('mobile.astrologer')}
            </button>
          </div>}

        {/* Language */}
        <div className="flex items-center justify-center gap-1 pt-1" data-id="element-16050">
          {langOptions.map(lang => <button key={lang.code} onClick={() => setLanguage(lang.code)} className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${language === lang.code ? 'bg-slate-100 text-slate-800' : 'text-slate-400'}`} data-id="element-16051">
              {lang.flag} {lang.shortLabel}
            </button>)}
        </div>
      </div>
    </div>;
}